import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { validateCircuit, type CircuitComponent, type CircuitTerminal, type WireConnection } from './physics/circuitSolver';

export interface BenchComponent extends CircuitComponent {
  x: number;
  y: number;
}

interface LabBenchProps {
  components: BenchComponent[];
  wires: WireConnection[];
  energized: boolean;
  supplyVoltage: number;
  ammeterReading?: number | null;
  voltmeterReading?: number | null;
  ammeterRange: number;
  onComponentsChange: (components: BenchComponent[]) => void;
  onWiresChange: (wires: WireConnection[]) => void;
  onSwitchChange: (closed: boolean) => void;
}

const W = 1160;
const H = 620;
const sizes: Record<string, { width: number; height: number }> = {
  battery: { width: 150, height: 110 },
  rheostat: { width: 180, height: 120 },
  ammeter: { width: 140, height: 140 },
  resistor: { width: 180, height: 100 },
  switch: { width: 130, height: 100 },
  voltmeter: { width: 140, height: 140 },
};

function terminalPoint(component: BenchComponent, terminal: CircuitTerminal) {
  const size = sizes[component.type];
  switch (terminal) {
    case 'positive': return { x: component.x + size.width - 8, y: component.y + 56 };
    case 'negative': return { x: component.x + 8, y: component.y + 56 };
    case 'left': case 'input': case 'high': return { x: component.x + 4, y: component.y + size.height / 2 };
    case 'right': case 'output': case 'low': return { x: component.x + size.width - 4, y: component.y + size.height / 2 };
    default: return { x: component.x + size.width / 2, y: component.y + size.height / 2 };
  }
}

const terminalNames: Record<string, CircuitTerminal[]> = {
  battery: ['negative', 'positive'],
  rheostat: ['input', 'output'],
  ammeter: ['input', 'output'],
  resistor: ['left', 'right'],
  switch: ['input', 'output'],
  voltmeter: ['high', 'low'],
};

const colors = ['#e4572e', '#1677c8', '#69a84f', '#8c63bd', '#f0a202'];

const LabBench: React.FC<LabBenchProps> = ({
  components,
  wires,
  energized,
  supplyVoltage,
  ammeterReading,
  voltmeterReading,
  ammeterRange,
  onComponentsChange,
  onWiresChange,
  onSwitchChange,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [pendingTerminal, setPendingTerminal] = useState<{ componentId: string; terminal: CircuitTerminal } | null>(null);
  const [wireMessage, setWireMessage] = useState('Select a terminal, then select another terminal to connect a lead.');
  const [dragging, setDragging] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const validation = useMemo(() => validateCircuit(components, wires, { supplyVoltage, ammeterRange }), [components, wires, supplyVoltage, ammeterRange]);
  const byId = useMemo(() => new Map(components.map((component) => [component.id, component])), [components]);

  const toSvgPoint = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * W / rect.width, y: (event.clientY - rect.top) * H / rect.height };
  };

  const handleComponentPointerDown = (event: React.PointerEvent<SVGGElement>, component: BenchComponent) => {
    if ((event.target as Element).closest('[data-terminal="true"]')) return;
    const point = toSvgPoint(event as unknown as React.PointerEvent<SVGSVGElement>);
    setDragging({ id: component.id, dx: point.x - component.x, dy: point.y - component.y });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragging) return;
    const point = toSvgPoint(event);
    onComponentsChange(components.map((component) => component.id === dragging.id
      ? { ...component, x: Math.max(16, Math.min(W - sizes[component.type].width - 16, point.x - dragging.dx)), y: Math.max(20, Math.min(H - sizes[component.type].height - 16, point.y - dragging.dy)) }
      : component));
  };

  const connectTerminal = (componentId: string, terminal: CircuitTerminal) => {
    const selected = { componentId, terminal };
    if (!pendingTerminal) {
      setPendingTerminal(selected);
      setWireMessage(`Start selected: ${byId.get(componentId)?.type} ${terminal}. Choose a destination terminal.`);
      return;
    }
    if (pendingTerminal.componentId === componentId) {
      setPendingTerminal(null);
      setWireMessage('A lead must connect two different apparatus. Start again from a terminal.');
      return;
    }
    const exists = wires.some((wire) => (
      (wire.from.componentId === pendingTerminal.componentId && wire.from.terminal === pendingTerminal.terminal && wire.to.componentId === componentId && wire.to.terminal === terminal)
      || (wire.to.componentId === pendingTerminal.componentId && wire.to.terminal === pendingTerminal.terminal && wire.from.componentId === componentId && wire.from.terminal === terminal)
    ));
    if (exists) {
      setPendingTerminal(null);
      setWireMessage('Those terminals are already connected.');
      return;
    }
    onWiresChange([...wires, { id: `wire-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`, from: pendingTerminal, to: selected }]);
    setPendingTerminal(null);
    setWireMessage('Lead connected. Continue wiring or use the circuit check.');
  };

  const componentLabel = (component: BenchComponent) => ({
    battery: 'DC supply', rheostat: 'Rheostat', ammeter: 'Ammeter', resistor: 'Resistance wire', voltmeter: 'Voltmeter', switch: 'Switch / key',
  }[component.type]);
  const needlePoint = (reading: number | null | undefined, fullScale: number) => {
    const fraction = Math.max(0, Math.min(1, (reading ?? 0) / fullScale));
    const angle = Math.PI + fraction * Math.PI;
    return { x: 70 + 36 * Math.cos(angle), y: 70 + 36 * Math.sin(angle) };
  };
  const keyClosed = components.find((component) => component.type === 'switch')?.closed === true;

  const renderDevice = (component: BenchComponent) => {
    const commonProps = {
      onPointerDown: (event: React.PointerEvent<SVGGElement>) => handleComponentPointerDown(event, component),
      style: { cursor: dragging?.id === component.id ? 'grabbing' as const : 'grab' as const },
    };
    const terminals = (terminalNames[component.type] ?? []).map((terminal) => {
      const point = terminalPoint(component, terminal);
      return (
        <Tooltip key={terminal} title={`${componentLabel(component)} ${terminal} terminal — click to connect a lead`}>
          <circle
            data-terminal="true"
            cx={point.x - component.x}
            cy={point.y - component.y}
            r="9"
            fill={pendingTerminal?.componentId === component.id && pendingTerminal.terminal === terminal ? '#f7c948' : '#f7fafc'}
            stroke={terminal === 'positive' || terminal === 'high' ? '#d94b44' : '#34495e'}
            strokeWidth="3"
            tabIndex={0}
            role="button"
            aria-label={`${componentLabel(component)} ${terminal} terminal`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); connectTerminal(component.id, terminal); }}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); connectTerminal(component.id, terminal); } }}
          />
        </Tooltip>
      );
    });

    return (
      <g key={component.id} transform={`translate(${component.x} ${component.y})`} {...commonProps}>
        {component.type === 'battery' && <>
          <rect x="12" y="18" width="126" height="70" rx="14" fill="url(#metal)" stroke="#748398" strokeWidth="2" />
          <rect x="0" y="43" width="12" height="20" rx="4" fill="#cf4b45" />
          <rect x="138" y="43" width="12" height="20" rx="4" fill="#536273" />
          <text x="30" y="48" className="bench-device-title">DC SUPPLY</text>
          <text x="30" y="70" className="bench-device-value">{supplyVoltage.toFixed(1)} V</text>
          <text x="140" y="34" className="bench-polarity">+</text><text x="-2" y="34" className="bench-polarity">−</text>
        </>}
        {component.type === 'rheostat' && <>
          <rect x="12" y="22" width="156" height="66" rx="12" fill="url(#metal)" stroke="#748398" strokeWidth="2" />
          <path d="M35 58 C55 30 80 86 102 58 S145 38 148 58" fill="none" stroke="#bb573f" strokeWidth="6" />
          <line x1="90" y1="30" x2="90" y2="72" stroke="#17324d" strokeWidth="4" />
          <text x="24" y="108" className="bench-device-title">RHEOSTAT · {component.resistance ?? 0} Ω</text>
        </>}
        {component.type === 'ammeter' && <>
          <circle cx="70" cy="70" r="56" fill="url(#meterFace)" stroke="#506176" strokeWidth="5" />
          <path d="M31 78 A42 42 0 0 1 109 78" fill="none" stroke="#30445d" strokeWidth="2" />
          <path d="M35 73 l-5 -4 M49 46 l-4 -6 M70 38 v-8 M91 46 l4 -6 M105 73 l5 -4" stroke="#30445d" strokeWidth="2" />
          <line x1="70" y1="70" x2={needlePoint(ammeterReading, 2).x} y2={needlePoint(ammeterReading, 2).y} stroke="#e4572e" strokeWidth="3" />
          <circle cx="70" cy="70" r="5" fill="#26364a" />
          <text x="70" y="96" textAnchor="middle" className="bench-meter-symbol">A</text>
          <text x="70" y="127" textAnchor="middle" className="bench-device-title">AMMETER</text>
        </>}
        {component.type === 'voltmeter' && <>
          <circle cx="70" cy="70" r="56" fill="url(#meterFace)" stroke="#506176" strokeWidth="5" />
          <path d="M31 78 A42 42 0 0 1 109 78" fill="none" stroke="#30445d" strokeWidth="2" />
          <path d="M35 73 l-5 -4 M49 46 l-4 -6 M70 38 v-8 M91 46 l4 -6 M105 73 l5 -4" stroke="#30445d" strokeWidth="2" />
          <line x1="70" y1="70" x2={needlePoint(voltmeterReading, 12).x} y2={needlePoint(voltmeterReading, 12).y} stroke="#168a9a" strokeWidth="3" />
          <circle cx="70" cy="70" r="5" fill="#26364a" />
          <text x="70" y="96" textAnchor="middle" className="bench-meter-symbol">V</text>
          <text x="70" y="127" textAnchor="middle" className="bench-device-title">VOLTMETER</text>
        </>}
        {component.type === 'resistor' && <>
          <rect x="10" y="31" width="160" height="42" rx="5" fill="#dbc7a9" stroke="#8f7758" strokeWidth="2" />
          <path d="M25 52 h14 l8 -13 l14 26 l14 -26 l14 26 l14 -26 l14 26 l9 -13 h25" fill="none" stroke="#984a38" strokeWidth="4" />
          <text x="90" y="96" textAnchor="middle" className="bench-device-title">RESISTANCE WIRE · {component.resistance ?? 0} Ω</text>
        </>}
        {component.type === 'switch' && <>
          <rect x="12" y="22" width="106" height="58" rx="12" fill="url(#metal)" stroke="#748398" strokeWidth="2" />
          <circle cx="38" cy="52" r="6" fill="#33485f" /><circle cx="91" cy="52" r="6" fill="#33485f" />
          <line x1="38" y1="52" x2={component.closed ? 91 : 79} y2={component.closed ? 52 : 32} stroke={component.closed ? '#d94b44' : '#6c7a8a'} strokeWidth="5" strokeLinecap="round" />
          <text x="65" y="100" textAnchor="middle" className="bench-device-title">KEY · {component.closed ? 'CLOSED' : 'OPEN'}</text>
        </>}
        {terminals}
      </g>
    );
  };

  const wireElements = wires.map((wire, index) => {
    const fromComponent = byId.get(wire.from.componentId);
    const toComponent = byId.get(wire.to.componentId);
    if (!fromComponent || !toComponent) return null;
    const from = terminalPoint(fromComponent, wire.from.terminal);
    const to = terminalPoint(toComponent, wire.to.terminal);
    const midX = (from.x + to.x) / 2;
    const bend = Math.max(36, Math.min(110, Math.abs(to.y - from.y) * 0.5 + 42));
    const d = `M ${from.x} ${from.y} C ${midX} ${from.y + bend}, ${midX} ${to.y - bend}, ${to.x} ${to.y}`;
    return <path key={wire.id} d={d} fill="none" stroke={energized ? (index % 2 ? '#e75a45' : '#f2a541') : colors[index % colors.length]} strokeWidth="8" strokeLinecap="round" className={energized ? 'bench-wire bench-wire-live' : 'bench-wire'} />;
  });

  return (
    <Paper className="ohm-bench-shell" elevation={0}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ p: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>Circuit bench</Typography>
          <Typography variant="body2" color="text.secondary">Drag apparatus to arrange the bench; click two terminals to connect a lead.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip color={validation.energized ? 'success' : 'default'} label={validation.energized ? 'Circuit energized' : 'Circuit open'} />
          <Button size="small" variant={keyClosed ? 'contained' : 'outlined'} color={keyClosed ? 'success' : 'primary'} onClick={() => onSwitchChange(!keyClosed)} aria-pressed={keyClosed}>{keyClosed ? 'Open key' : 'Close key'}</Button>
          <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={() => { onWiresChange(wires.slice(0, -1)); setWireMessage('Last lead removed.'); }}>Remove last lead</Button>
        </Stack>
      </Stack>
      <Box className="ohm-bench-canvas-wrap">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Interactive Ohm's law virtual circuit bench" onPointerMove={handlePointerMove} onPointerUp={() => setDragging(null)} onPointerCancel={() => setDragging(null)}>
          <defs>
            <linearGradient id="benchWood" x2="0" y2="1"><stop offset="0" stopColor="#e6c89c"/><stop offset="1" stopColor="#c59a67"/></linearGradient>
            <linearGradient id="metal" x2="0" y2="1"><stop offset="0" stopColor="#f7fafc"/><stop offset="0.5" stopColor="#dbe3ec"/><stop offset="1" stopColor="#aebdcd"/></linearGradient>
            <radialGradient id="meterFace"><stop offset="0" stopColor="#fff"/><stop offset="1" stopColor="#dce5ed"/></radialGradient>
            <filter id="benchShadow" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#172b42" floodOpacity="0.22" /></filter>
          </defs>
          <rect width={W} height={H} fill="#dce5eb" />
          <rect x="15" y="15" width={W - 30} height={H - 30} rx="24" fill="url(#benchWood)" stroke="#a57a4d" strokeWidth="8" filter="url(#benchShadow)" />
          <path d="M40 110 H1120 M40 340 H1120 M40 560 H1120" stroke="#af865d" strokeWidth="2" opacity="0.22" />
          <g>{wireElements}</g>
          <g>{components.map(renderDevice)}</g>
        </svg>
      </Box>
      <Box sx={{ p: 2, pt: 0 }}>
        <Alert severity={validation.shortCircuit ? 'error' : validation.warnings.length ? 'warning' : 'success'}>{validation.warnings[0] || wireMessage || 'Wiring looks good.'}</Alert>
        {!!validation.errors.length && <Alert severity="error" sx={{ mt: 1 }}>{validation.errors.join(' ')}</Alert>}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Touch-safe low-voltage simulation. Instrument readings include loading, resolution, and small bounded reading uncertainty.
        </Typography>
      </Box>
    </Paper>
  );
};

export default LabBench;
